import React, { Component } from 'react';
import {
	Button,
	Row,
	Col,
	Form, FormGroup, Label, Input
	} from 'reactstrap';
import Butt from './Butt';
import ActionLink from './ActionLink';
import API from '../API';
import { Apify, ApifyParallel } from '../Apify';
import I18N from '../I18N';
import { T } from '../traks';
import { R } from '../routes';

import LogoAndInfo from './LogoAndInfo';
import XTab from './XTab';
import MU from '../MUTIL';
import { Modal, ModalOpen, ModalClose } from '../Modal';
import { connect } from 'react-redux';
import { parse_date, validatePremium } from '../misc';

function TradeInit(exchange, ticker, portfolio_id, type) {
	ModalOpen("TradeModal", {exchange, ticker, portfolio_id, type});
}

function close_trade_dialog() {
	ModalClose();
}

function TradeAbort() {
	close_trade_dialog();
}

class TradeContents extends Component {
	constructor(props) {
		super(props);
		this.state = {
			buysell_xtab_index: 0,
			buy_spend: 0,
			buy_quantity: 0,
			post_in_flight: false,
			...this.get_initial_sell_state_from_props(),
		};
	}

	get_trade_type() {
		const idx = (this.state && this.state.buysell_xtab_index) || 0;
		const popts = this.props.trade_portfolio_options || {};

		const tabs = [];
		if (popts.Bankroll > 0 && popts.CanBuy) tabs.push('buy');
		if ((popts.ShortingEnabled && popts.LongQuantity > 0) || (!popts.ShortingEnabled && popts.CurrentStockQuantity > 0)) tabs.push('sell');
		if (popts.ShortingEnabled) tabs.push('short');
		if (popts.ShortingEnabled && popts.CurrentStockQuantity < 0) tabs.push('cover');

		return tabs[idx] || 'buy';
	}

	get_initial_sell_state_from_props() {
		const popts = this.props.trade_portfolio_options || {};
		const val = popts.CurrentStockQuantity || 0;

		const safeQuantity =
			popts.ShortingEnabled && val < 0 ? 0 : val;

		return {
			sell_quantity: safeQuantity,
			sell_receive: this.get_target_info("sell_receive").calc_from_opposite(safeQuantity),
		};
	}

	componentDidUpdate(prev_props) {
		if (this.props.trade_portfolio_options.GameId !== prev_props.trade_portfolio_options.GameId) {
			this.setState(this.get_initial_sell_state_from_props());
		}
	}

	calc_a_bunch_of_stuff() {
		const opts = this.props.trade_options;
		const popts = this.props.trade_portfolio_options;

		const brokerage_ratio = (1 + popts.BrokeragePct / 100);
		const effective_buy_rate = popts.StockRateInGameCurrency * brokerage_ratio;
		const effective_sell_rate = popts.StockRateInGameCurrency / brokerage_ratio;

		let available_to_spend  = popts.AvailableToSpend;

		const buy_quantity_max = MU.floor_to_decimals(available_to_spend / effective_buy_rate, opts.NDecimals);
		const sell_receive_max = popts.CurrentStockQuantity * effective_sell_rate;

		return {
			available_to_spend,
			buy_quantity_max,
			sell_receive_max,
			effective_buy_rate,
			effective_sell_rate,
		};
	}

	get_target_info(target, tradeType = this.get_trade_type()) {
		const opts = this.props.trade_options;
		const popts = this.props.trade_portfolio_options;

		const {
			buy_quantity_max,
			available_to_spend,
			sell_receive_max,
			effective_buy_rate,
			effective_sell_rate,
		} = this.calc_a_bunch_of_stuff();

		const remainingShortCapacity = Math.max(
			(popts.ShortingLimit || 0) - (popts.ShortExposure || 0),
			0
		);

		const info = (() => {
			switch (target) {
				case "buy_quantity":
				case "sell_quantity":
					return { n_decimals: opts.NDecimals, rounder: MU.floor_to_decimals };
				case "buy_spend":
				case "sell_receive":
					return { n_decimals: 2, rounder: MU.ceil_to_decimals };
				default:
					throw new Error("invalid target: " + target);
			}
		})();

		const maxByType = {
			buy: {
				buy_quantity: buy_quantity_max,
				buy_spend: available_to_spend,
			},
			sell: {
				sell_quantity: popts.CurrentStockQuantity,
				sell_receive: sell_receive_max,
			},
			short: {
				sell_quantity: remainingShortCapacity / effective_sell_rate,
				sell_receive: remainingShortCapacity,
			},
		}[tradeType] || {};

		const max = maxByType[target] || 0;

		const scalar = {
			buy_quantity: 1 / effective_buy_rate,
			buy_spend: effective_buy_rate,
			sell_quantity: 1 / effective_sell_rate,
			sell_receive: effective_sell_rate,
		}[target];

		const correct = (value) => info.rounder(Math.min(max, value), info.n_decimals);

		return {
			...info,
			max,
			correct,
			scalar,
			calc_from_opposite: (opposite_value) => correct(opposite_value * scalar),
		};
	}

	get_exposure_state() {
		const popts = this.props.trade_portfolio_options || {};
		const queueRaw = Array.isArray(this.props.queue) ? this.props.queue : [];
		const stockId = popts.StockId;

		const queueForStock = queueRaw.filter((qt) => {
			if (stockId) {
				return qt.StockId === stockId;
			}
			return qt.Exchange === this.props.exchange && qt.Ticker === this.props.ticker;
		});

		const currentStockQuantity = popts.CurrentStockQuantity != null ? popts.CurrentStockQuantity : 0;
		const currentLong = popts.LongQuantity != null ? popts.LongQuantity : Math.max(currentStockQuantity, 0);
		const currentShort = popts.ShortQuantity != null ? popts.ShortQuantity : Math.max(-currentStockQuantity, 0);

		const hasQueuedLongIncrease = queueForStock.some(
			(qt) => qt.Type === 'long' && ((qt.BuyWithAmount != null ? qt.BuyWithAmount : 0) > 0)
		);
		const hasQueuedShortIncrease = queueForStock.some(
			(qt) => qt.Type === 'short' && ((qt.SellQuantity != null ? qt.SellQuantity : 0) > 0)
		);

		return {
			queueForStock,
			currentLong,
			currentShort,
			hasQueuedLongIncrease,
			hasQueuedShortIncrease,
			hasLongExposure: currentLong > 0 || hasQueuedLongIncrease,
			hasShortExposure: currentShort > 0 || hasQueuedShortIncrease,
		};
	}


	render() {
		const opts = this.props.trade_options;

		const eligible_portfolios = this.props.eligible_portfolios || [];
		const popts = this.props.trade_portfolio_options;
		const exposureState = this.get_exposure_state();

		const selected_portfolio_id = this.props.portfolio_id;
		const selected_portfolio = eligible_portfolios.find(p => p.PortfolioId === selected_portfolio_id) || {};

		const gameTradeInfo = selected_portfolio.GameTradeInfo || {};
		const userTrades = (selected_portfolio.UserPortfolioTrades && selected_portfolio.UserPortfolioTrades) || 0;

		const showTradeInfo = gameTradeInfo.tradeAmountActivated || false;

		// If the trade amount is activated and userTrades is greater than the amount defined, disable the trade button
		const tradeLimitReached = gameTradeInfo.tradeAmountActivated && userTrades >= gameTradeInfo.tradeAmount;

		// If risk values setting is activated and beta is undefined or less than 0, disable the trade button
		const betaNotAllowed = gameTradeInfo.riskValues && (popts.BetaValue === undefined || popts.BetaValue <= 0)

		// Message to display if trading due to beta is disabled
		let betaAllowedMessage = undefined;
		if (betaNotAllowed) {
			betaAllowedMessage = (
				<div className="d-flex flex-row justify-content-between w-100 font-small pt-3">
					<div className='ad_trade_middletext'><T>Trading disabled due to negative or missing risk score.</T></div>
				</div>
			)
		}
		
		// Message to display if trading due to limit is hit
		let tradeInfoMessage = undefined;
		if (showTradeInfo) {
			tradeInfoMessage = (
				<div className="d-flex flex-row justify-content-between w-100 font-small pt-3">
				<div className='ad_trade_middletext'><T>You have used {userTrades} of {gameTradeInfo.tradeAmount} trades</T></div>
				</div>
			)
		}

		var rate_element;
		if (popts) {
			const xr = I18N.floatval_unit(popts.StockRateInExchangeCurrency, 2, popts.ExchangeCurrency);
			const gr = I18N.floatval_unit(popts.StockRateInGameCurrency, 2, popts.GameCurrency);
			if (popts.ExchangeCurrency === popts.GameCurrency) {
				rate_element = <span>{gr}</span>;
			} else {
				rate_element = <span>{gr} ({xr})</span>;
			}
			rate_element = <span>1 {this.props.ticker} = {rate_element}</span>;
		}

        var beta_element;
        if (popts && validatePremium(this.props.me) === true && popts.BetaValue !== 0) {
            beta_element = <span><T>Risk Score</T>: <span>{I18N.floatval(popts.BetaValue, 4)}</span></span>;
        }

		const header = (
			<Row className="pb-3">
				<Col>
					<div className="d-flex">
						<div className="mr-auto ad_trade_header">
							<LogoAndInfo exchange={this.props.exchange} ticker={this.props.ticker} hasData={false} />
						</div>
						<div className='d-flex flex-column'>
							{rate_element}
                            {beta_element}
						</div>
					</div>
				</Col>
			</Row>
		);

		if (!this.props.me) {
			return (
				<div className="ad_trade_bg p-4">
					{header}
					You are not logged in. Please login or
					create an account to begin trading.
				</div>
			);
		}

		const choose_game = (() => {
			if (eligible_portfolios.length === 0) {
				const start_a_game = () => {
					TradeAbort();
					R.go("/new_game");
				};
				return (
					<div>
						<Row><Col>
							You currently have no active game in which this stock can be traded.
						</Col></Row>
						<Row><Col>
							<Butt onClick={start_a_game}>Start a game</Butt>
						</Col></Row>
					</div>
				);
			}
			const options = eligible_portfolios.map(p => (
				<option key={p.PortfolioId} value={p.PortfolioId}>{p.Name}</option>
			));
			const change_portfolio = (e) => {
				this.props.set_portfolio_id(parseInt(e.target.value, 10));
			};
			return (
				<Row>
					<Col>
						<FormGroup>
							<Label for="trade_game_select"><T>Choose game</T></Label>
							<Input className="ad_trade_gamedropdown font-small" type="select" id="trade_game_select" value={this.props.portfolio_id} onChange={change_portfolio}>
								{options}
							</Input>
						</FormGroup>
					</Col>
				</Row>
			);
		})();

		const {
			buy_quantity_max,
			available_to_spend,
			sell_receive_max,
			effective_buy_rate,
			effective_sell_rate,
		} = this.calc_a_bunch_of_stuff();

		const available_to_spend_element = I18N.floatval_unit(available_to_spend, 2, popts.GameCurrency);
		let current_quantity = I18N.floatval(popts.CurrentStockQuantity, opts.NDecimals);
		let current_shorted_quantity = null;

		if (popts.ShortingEnabled && popts.LongQuantity) {
			current_quantity = I18N.floatval(popts.LongQuantity, opts.NDecimals);
		}

		if (popts.ShortingEnabled && popts.ShortQuantity) {
			current_shorted_quantity = I18N.floatval(popts.ShortQuantity, opts.NDecimals);
		}

		var max_per_equity_element = null;
		if(popts.MaxAmountPerStock > 0) {
			max_per_equity_element = (
				<div className="d-flex flex-row justify-content-between w-100 font-small pt-2">
					<div className="w-100 ad_max_per_equity_text p-1"><T>There's a maximum limit of {I18N.floatval_unit(popts.MaxAmountPerStock, 2, popts.GameCurrency)} per equity in this game</T></div>
				</div>
			);
		}
		const inventory_element = (
			<div className='pb-4'>
				{max_per_equity_element}
				<div className="d-flex flex-row justify-content-between w-100 font-small pt-2">
					<div className="ad_trade_middletext"><T>Available funds:</T></div>
					<div className="ad_trade_funds_available px-1">{available_to_spend_element}</div>
				</div>
				<div className="d-flex flex-row justify-content-between w-100 font-small">
					<div className="ad_trade_middletext"><T>Stocks in game:</T></div>
					<div>{current_quantity}</div>
				</div>
				{popts.ShortingEnabled && popts.ShortQuantity !== null && (
					<div className="d-flex flex-row justify-content-between w-100 font-small">
						<div className="ad_trade_middletext"><T>Shorted stocks in game:</T></div>
						<div>{current_shorted_quantity}</div>
					</div>
				)}
			</div>
		);

		const set_buysell_xtab_index = (buysell_xtab_index) => {
			this.setState({buysell_xtab_index});
		};

		var buysell_tabs = [];
		if (popts.Bankroll > 0 && popts.CanBuy) {
			buysell_tabs.push({ label: <T>BUY</T>, type: 'buy' });
		}

		if ((popts.ShortingEnabled && popts.LongQuantity > 0) || (!popts.ShortingEnabled && popts.CurrentStockQuantity > 0)) {
			buysell_tabs.push({label: <T>SELL</T>, type: 'sell'});
		}

		if (popts.ShortingEnabled) {
			buysell_tabs.push({label: <T>SHORT </T>, type: 'short'});
		}

		if (popts.ShortingEnabled && popts.ShortQuantity >= 0) {
			buysell_tabs.push({label: <T>COVER</T>, type: 'cover'});
		}

		const selected_tab = buysell_tabs[this.state.buysell_xtab_index]
			? buysell_tabs[this.state.buysell_xtab_index].type
			: (buysell_tabs.length > 0 ? buysell_tabs[0].type : 'buy');

		const tab_labels = buysell_tabs.map(tab => (
			<div key={tab.type} className="ad_trade_buysell_select">
				{tab.label}
			</div>
		));

		const get_opposite_target = (target) => {
			switch (target) {
			case "buy_spend": return "buy_quantity";
			case "buy_quantity": return "buy_spend";
			case "sell_quantity": return "sell_receive";
			case "sell_receive": return "sell_quantity";
			case "short_spend": return "short_quantity";
			case "short_quantity": return "short_spend";
			default:
				throw new Error("invalid target: " + target);
			}
		};

		const input_update = (target, value) => {
			const opposite_target = get_opposite_target(target);
			const opposite_target_info = this.get_target_info(opposite_target);
			this.setState({
				[target]: value,
				[opposite_target]: opposite_target_info.calc_from_opposite(value),
			})
		};

		const clear_zero_input = (e) => {
			if(e.target.value === "0") {
				e.target.value = '';
			}
		}

		const change_float = (e) => {
			input_update(e.target.name, e.target.value + "");
		};

		const blur_float = (e) => {
			if (typeof this.state[e.target.name] === "number") {
				/* if type is number, the value was derived
				 * rather than entered, so leave it alone to
				 * avoid weirdness when tabbing around */
				return
			}
			const target = e.target.name;
			const value = e.target.value;
			const target_info = this.get_target_info(target);
			input_update(target, target_info.correct(value));
		};

		var buysell_ui_element;
		const stock_step = Math.pow(10, -opts.NDecimals);
		var execute_text_element;
		var disable_trade_button = false;

		var trade_request_portfolio_id;
		for (const p of eligible_portfolios) {
			if (trade_request_portfolio_id === undefined || p.PortfolioId === this.props.portfolio_id) {
				trade_request_portfolio_id = p.PortfolioId;
			}
		}

		var trade_request = {
			PortfolioId: trade_request_portfolio_id,
			Exchange: this.props.exchange,
			Ticker: this.props.ticker,
			Type: this.props.type
		};

		const exchange = this.props.exchange;
		const date = I18N.datetime_short(new Date(opts.ExchangeOpensAtTs * 1000));

		var notice = null;
		var has_trade_delay = false;

		if (opts.TradeDelaySeconds) {
			has_trade_delay = true;
			const trade_delay_minutes = Math.floor(opts.TradeDelaySeconds / 60);
			const on_read_more_here = () => {
				TradeAbort();
				R.go("/faq");
			}
			notice = (
				<div className="mt-3 ad_trade_notice">*
				<T>Be aware that this trade will go through in {trade_delay_minutes} minutes with the quote at
				that time. This is to avoid the difference between live data and game data. <ActionLink on_click={on_read_more_here}>Read more here</ActionLink>
				</T>
				</div>
			);
		}

		var must_enqueue = has_trade_delay;

		const has_data = !!popts.StockRateInExchangeCurrency;

		if (!has_data) {
			const expected_ipo_date = parse_date(opts.ExpectedIPODate);
			must_enqueue = true;
			if (expected_ipo_date) {
				const expected_ipo_date_element = I18N.date(expected_ipo_date);
				notice = (
					<div className="mt-3 ad_trade_notice">*
					<T>The stock is scheduled for IPO on {expected_ipo_date_element};
					the trade will execute as soon as we
					have the initial data.</T>
					</div>
				);
			} else {
				notice = (
					<div className="mt-3 ad_trade_notice">*
					<T>The stock is not currently available
					for sale due to missing data. The trade
					will execute as soon as we have data.</T>
					</div>
				);
			}
		}

		if (selected_tab === 'buy') {
			const can_buy_now = opts.CanBuy && opts.ExchangeIsOpen;
			let invoice = [];
			const to_buy = this.get_target_info("buy_quantity").correct(this.state.buy_quantity);
			const total = to_buy * effective_buy_rate;
			const brokerage = total * (popts.BrokeragePct / 100);
			const to_spend = total - brokerage;
			const quantity_element = <span>{I18N.floatval(to_buy, opts.NDecimals)} {this.props.ticker}</span>;

			execute_text_element = (must_enqueue || !can_buy_now) ? <span><T>Enqueue</T></span> : <span><T>Buy</T></span>;
			disable_trade_button = to_buy === 0 || tradeLimitReached || betaNotAllowed;
			const longBlockedByShort = exposureState.hasShortExposure;
			if (longBlockedByShort) {
				disable_trade_button = true;
			}
			let longExposureWarning = null;
			if (longBlockedByShort) {
				longExposureWarning = (
					<div className="pt-2 ad_risk_red font-small">
						<T>You already have an active short position or queued short order for this stock. Cover or cancel it before buying.</T>
					</div>
				);
			}

			trade_request.BuyWithAmount = parseFloat(this.state.buy_spend);
			trade_request.Type = "long";
			trade_request.DoEnqueue = !can_buy_now;

			if (has_data && !can_buy_now) {
				if (!opts.ExchangeIsOpen) {
					notice = (<div className="mt-3 ad_trade_notice">
						<T>*The exchange ({exchange}) is not
						currently open, but you can enqueue a
						purchase request which will be executed
						when the exchange opens ({date}). The
						amount you choose to spend and the
						stock rate at the time the exchange
						opens will determine the quantity you
						receive.</T>
					</div>);
				} else {
					notice = (<div className="mt-3 ad_trade_notice">
						<T>*The stock cannot currently
						be traded, but you can enqueue
						a purchase request which will
						be executed when trading is
						resumed. The amount you choose
						to spend and the stock rate at
						the time trading resumes will
						determine the quantity you
						receive.</T>
					</div>);
				}
			}

			if (has_data) {
				if (popts.BrokeragePct > 0) {
					invoice.push(
						<div key="trade" className="d-flex ad_trade_invoice">
							<div className="mr-auto">{quantity_element}</div>
							<div>{I18N.floatval_unit(to_spend, 2, popts.GameCurrency)}</div>
						</div>
					);

					const brokerage_pct_element = I18N.floatval(popts.BrokeragePct, 2);
					invoice.push(
						<div key="brokerage" className="d-flex ad_trade_invoice">
							<div className="mr-auto"><T>Brokerage</T> ({brokerage_pct_element }%)</div>
							<div>{I18N.floatval_unit(brokerage, 2, popts.GameCurrency)}</div>
						</div>
					);

					invoice.push(
						<div key="total" className="d-flex ad_trade_invoice">
							<div className="mr-auto w-100 ad_trade_invoice_bold"><T>Total</T></div>
							<div className="text-right ad_trade_invoice_underline_total">{I18N.floatval_unit(total, 2, popts.GameCurrency)}</div>
						</div>
					);
				} else {
					invoice.push(
						<div key="trade_total" className="d-flex ad_trade_invoice">
							<div className="mr-auto w-100 ad_trade_invoice_bold">{quantity_element}</div>
							<div className="text-right ad_trade_invoice_underline_total">{I18N.floatval_unit(to_spend, 2, popts.GameCurrency)}</div>
						</div>
					);
				}
			}

			buysell_ui_element = (
				<div>
					<Row><Col>
						<div className="ad_trade_middletext font-small"><T>Amount to spend</T></div>
						<div className="d-flex align-items-end">
							<input
								name="buy_spend"
								value={this.state.buy_spend}
								min={0}
								max={available_to_spend}
								step={0.01}
								type="number"
								className="mr-auto w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={change_float}
								onBlur={blur_float}
								onFocus={clear_zero_input}
							/>
							<div className="ad_trade_amountprice_unit">{popts.GameCurrency}</div>
						</div>
					</Col></Row>
					{has_data && (
					<Row><Col>
						<div className="ad_trade_middletext font-small pt-3"><T>Quantity to buy</T></div>
						<div className="d-flex align-items-end">
							<input
								name="buy_quantity"
								value={this.state.buy_quantity}
								min={0}
								max={buy_quantity_max}
								step={stock_step}
								type="number"
								className="w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={change_float}
								onBlur={blur_float}
								onFocus={clear_zero_input}
							/>
							<span className="ad_trade_amountprice_unit">{this.props.ticker}</span>
						</div>
					</Col></Row>
					)}
					<Row className="pt-3"><Col>
						{invoice}
					</Col></Row>
					{notice}
					{longExposureWarning}
				</div>
			);
		} else if (selected_tab === 'sell') {
			if (
				popts.ShortingEnabled &&
				popts.LongQuantity > 0 &&
				this.state.sell_quantity <= 0
			) {
				this.setState({
					sell_quantity: popts.LongQuantity,
					sell_receive: (popts.LongQuantity || 0) * effective_sell_rate,
				});
			}
			const can_sell_now = opts.CanBuy && opts.ExchangeIsOpen;
			let invoice = [];

			let to_sell = this.get_target_info("sell_quantity").correct(this.state.sell_quantity);

			if (popts.ShortingEnabled && popts.LongQuantity > 0) {
				to_sell = popts.LongQuantity;
			}

			const total = to_sell * effective_sell_rate;
			const brokerage = -total * (popts.BrokeragePct / 100);
			const to_receive = total - brokerage;
			
			let displayQuantity = to_sell;
			if (popts.ShortingEnabled) {
			displayQuantity = Math.max(popts.LongQuantity || 0, 0);
			}
			const quantity_element = (<span>{I18N.floatval(displayQuantity, opts.NDecimals)} {this.props.ticker}</span>);

			let sell_amount = popts.CurrentStockQuantity;
			let recieve_max = sell_receive_max;
			if (popts.ShortingEnabled && popts.LongQuantity) {
				sell_amount = popts.LongQuantity;
				recieve_max = (popts.LongQuantity || 0) * effective_sell_rate;
			}


			execute_text_element = (must_enqueue || !can_sell_now) ? <span><T>Enqueue</T></span> : <span><T>Sell</T></span>;
			disable_trade_button = to_sell === 0 || tradeLimitReached || betaNotAllowed;

			trade_request.SellQuantity = parseFloat(this.state.sell_quantity);
			trade_request.Type = "long";
			trade_request.DoEnqueue = !can_sell_now;

			if (has_data && !can_sell_now) {
				const exchange = this.props.exchange;
				const date = I18N.datetime_short(new Date(opts.ExchangeOpensAtTs * 1000));
				if (!opts.ExchangeIsOpen) {
					notice = (<Row className="mt-3 ad_trade_notice"><Col>
						<T>*The exchange ({exchange}) is not
						currently open, but you can enqueue a
						sale request which will be executed
						when the exchange opens ({date}). The
						quantity you choose to sell and the
						stock rate at the time the exchange
						opens will determine the amount you
						receive.</T>
					</Col></Row>);
				} else {
					notice = (<div className="mt-3 ad_trade_notice">
						<T>*The stock cannot currently
						be traded, but you can enqueue
						a sale request which will be
						executed when trading is
						resumed. The quantity you
						choose to sell and the stock
						rate at the time trading
						resumes will determine the
						amount you receive.</T>
					</div>);
				}
			}

			if (has_data) {
				if (popts.BrokeragePct > 0) {
					invoice.push(
						<div key="trade" className="d-flex ad_trade_invoice">
							<div className="mr-auto">{quantity_element}</div>
							<div>{I18N.floatval_unit(to_receive, 2, popts.GameCurrency)}</div>
						</div>
					);

					const brokerage_pct_element = I18N.floatval(popts.BrokeragePct, 2);
					invoice.push(
						<div key="brokerage" className="d-flex ad_trade_invoice">
							<div className="mr-auto"><T>Brokerage</T> ({brokerage_pct_element }%)</div>
							<div>{I18N.floatval_unit(brokerage, 2, popts.GameCurrency)}</div>
						</div>
					);

					invoice.push(
						<div key="total" className="d-flex ad_trade_invoice">
							<div className="mr-auto w-100 ad_trade_invoice_bold"><T>Total</T></div>
							<div className="text-right ad_trade_invoice_underline_total">{I18N.floatval_unit(total, 2, popts.GameCurrency)}</div>
						</div>
					);
				} else {
					invoice.push(
						<div key="trade_total" className="d-flex ad_trade_invoice">
							<div className="mr-auto w-100 ad_trade_invoice_bold">{quantity_element}</div>
							<div className="text-right ad_trade_invoice_underline_total">{I18N.floatval_unit(to_receive, 2, popts.GameCurrency)}</div>
						</div>
					);
				}
			}

			buysell_ui_element = (
				<div>
					<Row><Col>
						<div className="ad_trade_middletext font-small"><T>Quantity to sell</T></div>
						<div className="d-flex align-items-end">
							<input
								name="sell_quantity"
								value={this.state.sell_quantity}
								type="number"
								min={0}
								max={sell_amount}
								step={stock_step}
								className="mr-auto w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={change_float}
								onBlur={blur_float}
								onFocus={clear_zero_input}
							/>
							<div className="ad_trade_amountprice_unit">{this.props.ticker}</div>
						</div>
					</Col></Row>
					<Row><Col>
						<div className="ad_trade_middletext font-small pt-3"><T>Amount to receive</T></div>
						<div className="d-flex align-items-end">
							<input
								name="sell_receive"
								type="number"
								value={this.state.sell_receive}
								min={0}
								max={sell_receive_max}
								className="w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={change_float}
								onBlur={blur_float}
								onFocus={clear_zero_input}
							/>
							<div className="ad_trade_amountprice_unit">{popts.GameCurrency}</div>
						</div>
					</Col></Row>
					<Row className="pt-3"><Col>
						{invoice}
					</Col></Row>
					{notice}
				</div>
			);
		} else if (selected_tab === 'short') {
			const popts = this.props.trade_portfolio_options;
			const opts = this.props.trade_options;

			const brokerage_ratio = 1 + (popts.BrokeragePct / 100);
			const rate = popts.StockRateInGameCurrency || 0;
			const effective_sell_rate = rate / brokerage_ratio;

			const remainingShortCapacity = Math.max(
				(popts.ShortingLimit || 0) - (popts.ShortExposure || 0),
				0
			);

			const maxShortQuantity = remainingShortCapacity / (effective_sell_rate || 1);
			const can_short_now = opts.CanBuy && opts.ExchangeIsOpen;

			const to_short = parseFloat(this.state.sell_quantity) || 0;
			const overShortLimit = to_short > maxShortQuantity;

			execute_text_element = (must_enqueue || !can_short_now)
				? <span><T>Enqueue</T></span>
				: <span><T>Short</T></span>;

			disable_trade_button =
				to_short === 0 ||
				tradeLimitReached ||
				betaNotAllowed ||
				overShortLimit;
			const shortBlockedByLong = exposureState.hasLongExposure;
			if (shortBlockedByLong) {
				disable_trade_button = true;
			}

			trade_request.SellQuantity = to_short;
			trade_request.Type = "short";
			trade_request.DoEnqueue = !can_short_now;

			const shortWarnings = [];
			if (overShortLimit) {
				shortWarnings.push(
					<div key="short-limit" className="pt-2 ad_risk_red font-small">
						<T>You've reached your shorting limit. You can only short up to </T>
						{I18N.floatval_unit(remainingShortCapacity, 2, popts.GameCurrency)} <T> more.</T>
					</div>
				);
			}
			if (shortBlockedByLong) {
				shortWarnings.push(
					<div key="short-long-conflict" className="pt-2 ad_risk_red font-small">
						<T>You already have a long position or queued long order in this stock. Sell or cancel it before opening a short.</T>
					</div>
				);
			}

			buysell_ui_element = (
				<div>
					<Row><Col>
						<div className="ad_trade_middletext font-small"><T>Quantity to short</T></div>
						<div className="d-flex align-items-end">
							<input
								name="sell_quantity"
								type="number"
								value={this.state.sell_quantity}
								min={0}
								max={maxShortQuantity}
								step={Math.pow(10, -opts.NDecimals)}
								className="mr-auto w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={(e) => {
									const target_info = this.get_target_info("sell_quantity", "short");
									let val = parseFloat(e.target.value);
									if (isNaN(val)) val = 0;
									val = target_info.correct(val);
									this.setState({ sell_quantity: val });
								}}
								onFocus={clear_zero_input}
							/>
							<div className="ad_trade_amountprice_unit">{this.props.ticker}</div>
						</div>
					</Col></Row>

					<div className="d-flex flex-row justify-content-between w-100 font-small pt-2">
						<div className="ad_trade_middletext"><T>Short capacity remaining:</T></div>
						<div>{I18N.floatval_unit(remainingShortCapacity, 2, popts.GameCurrency)}</div>
					</div>

					{shortWarnings}
					{notice}
				</div>
			);
		} else if (selected_tab === 'cover') {
			const popts = this.props.trade_portfolio_options;
			console.log('Portfolio options: ', popts);
			const opts = this.props.trade_options;

			const brokerage_ratio = 1 + (popts.BrokeragePct / 100);
			const rate = popts.StockRateInGameCurrency || 0;
			const effective_buy_rate = rate * brokerage_ratio;

			const can_cover_now = opts.CanBuy && opts.ExchangeIsOpen;
			const has_short_position = popts.ShortQuantity > 0;

			const pending_cover_quantity = popts.PendingCoverQuantity || 0;
			const max_coverable_quantity = popts.MaxCoverableQuantity || 0;

			const short_quantity = Math.abs(popts.MaxCoverableQuantity || 0);

			let to_cover_amount = parseFloat(this.state.buy_with_amount) || 0;
			let to_cover_quantity = parseFloat(this.state.cover_quantity) || 0;

			if (to_cover_quantity > max_coverable_quantity) {
				to_cover_quantity = max_coverable_quantity;
			}
			if (to_cover_amount > max_coverable_quantity * effective_buy_rate) {
				to_cover_amount = max_coverable_quantity * effective_buy_rate;
			}

			const updateAmount = (val) => {
				const cappedAmount = Math.min(val, max_coverable_quantity * effective_buy_rate);
				this.setState({
					buy_with_amount: cappedAmount,
					cover_quantity: cappedAmount / effective_buy_rate,
				});
			};

			const updateQuantity = (val) => {
				const cappedQty = Math.min(val, max_coverable_quantity);
				this.setState({
					cover_quantity: cappedQty,
					buy_with_amount: cappedQty * effective_buy_rate,
				});
			};

			

			const overCoverLimit = to_cover_quantity > max_coverable_quantity;

			// Check if bankroll can fund the cover (bankroll falls by full buyback cost)
			let insufficient_funds = false;
			if (popts.ShortingEnabled && typeof popts.Bankroll === "number") {
				const coverCost = to_cover_quantity * effective_buy_rate;
				insufficient_funds = coverCost > popts.Bankroll + 1e-9;
			}

			disable_trade_button =
				to_cover_amount === 0 ||
				!has_short_position ||
				tradeLimitReached ||
				betaNotAllowed ||
				max_coverable_quantity <= 0 || insufficient_funds;
				

			execute_text_element = (must_enqueue || !can_cover_now)
				? <span><T>Enqueue</T></span>
				: <span><T>Cover</T></span>;

			trade_request.Type = "cover";
			trade_request.BuyWithAmount = to_cover_amount;
			trade_request.DoEnqueue = !can_cover_now;

			let coverWarning = null;
			if (max_coverable_quantity <= 0) {
				coverWarning = (
					<div className="pt-2 ad_risk_red font-small">
						<T>You’ve already queued covers for your entire short position.</T>
					</div>
				);
			} else if (overCoverLimit) {
				coverWarning = (
					<div className="pt-2 ad_risk_red font-small">
						<T>You can only cover up to</T> {I18N.floatval(max_coverable_quantity, opts.NDecimals)} {this.props.ticker}.
					</div>
				);
			} else if (insufficient_funds) {
				coverWarning = (
					<div className="pt-2 ad_risk_red font-small">
						<T>Covering this much would bring your bankroll below zero.</T>
					</div>
				);
			}

			buysell_ui_element = (
				<div>
					<Row><Col>
						<div className="ad_trade_middletext font-small"><T>Quantity to cover</T></div>
						<div className="d-flex align-items-end">
							<input
								name="cover_quantity"
								type="number"
								value={to_cover_quantity}
								min={0}
								max={max_coverable_quantity}
								step={Math.pow(10, -(opts.NDecimals || 2))}
								className="mr-auto w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={(e) => updateQuantity(parseFloat(e.target.value) || 0)}
								onFocus={clear_zero_input}
								disabled={max_coverable_quantity <= 0}
							/>
							<div className="ad_trade_amountprice_unit">{this.props.ticker}</div>
						</div>
					</Col></Row>

					<Row className="pt-2"><Col>
						<div className="ad_trade_middletext font-small"><T>Estimated cost</T></div>
						<div className="d-flex align-items-end">
							<input
								name="buy_with_amount"
								type="number"
								value={to_cover_amount}
								min={0}
								max={max_coverable_quantity * effective_buy_rate}
								step={Math.pow(10, -((popts.Game && popts.Game.BankrollNDecimals) || 2))}
								className="mr-auto w-100 ad_trade_amountprice_input ad_input_number_no_spinner"
								onChange={(e) => updateAmount(parseFloat(e.target.value) || 0)}
								onFocus={clear_zero_input}
								disabled={max_coverable_quantity <= 0}
							/>
							<div className="ad_trade_amountprice_unit">{popts.GameCurrency}</div>
						</div>
					</Col></Row>

					<div className="pt-2 font-small text-muted">
						<T>Current short position:</T> {I18N.floatval_unit(short_quantity, 0, this.props.ticker)}<br/>
						<T>Queued covers:</T> {I18N.floatval_unit(pending_cover_quantity, 0, this.props.ticker)}
					</div>

					{coverWarning}
				</div>
			);
		}

		const execute_trade = () => {
			if (disable_trade_button) {
				return;
			}
			this.setState({post_in_flight: true});
			API.post("/a/trade", {rq: trade_request}, (response) => {
				this.setState({post_in_flight: false});
				close_trade_dialog();
				R.go_or_reload("/game/:id", {id: popts.GameId});
			});
		};

		return (
			<Form>
				<div className="ad_trade_bg p-4">
					{header}
					{choose_game}
					{inventory_element}
					<XTab
						selected={this.state.buysell_xtab_index}
						onSelect={set_buysell_xtab_index}
						tabs={tab_labels}
					/>
					{buysell_ui_element}
					{tradeInfoMessage}
					{betaAllowedMessage}
					<Row className="pt-4">
						<Col xs="6">
							<Butt
								disabled={disable_trade_button || this.state.post_in_flight}
								className="ad_trade_buy_btn w-100 px-5 py-3"
								onClick={execute_trade}
							>
								{execute_text_element}
							</Butt>
						</Col>
						<Col xs="6">
							<Butt className="ad_trade_sell_btn w-100 px-5 py-3" onClick={TradeAbort}><T>Cancel</T></Butt>
						</Col>

					</Row>
				</div>
			</Form>
		);
	}
}

var TradePortfolioOptions = (props) => {
	if (props.portfolio_id && !props.trade_portfolio_options) return null;
	return <TradeContents {...props}/>;
}

TradePortfolioOptions = ApifyParallel(
	Apify("/a/trade_portfolio_options")
		.with_query_from_props((props) => {
			if (!props.portfolio_id) {
				return null;
			}
			return {
				portfolio_id: props.portfolio_id,
				exchange: props.exchange,
				ticker: props.ticker,
			};
		})
		.with_refresh_interval(10)
		.with_response_prop("trade_portfolio_options"),

	Apify("/a/trade_queue")
		.with_predicate((props) => !!props.portfolio_id)
		.with_query_from_props((props) => ({
			portfolio_id: props.portfolio_id,
		}))
		.with_4xx_prop("queue_error")
		.with_refresh_interval(15)
		.with_response_prop("queue")
).wrap(TradePortfolioOptions);

class TradeOptionsInner extends Component {
	constructor(props) {
		super(props);
		this.state = {portfolio_id: null};
	}

	render() {
		var portfolio_id = this.state.portfolio_id;

		const eligible_portfolios = this.props.eligible_portfolios || [];
		if (!portfolio_id) {
			for (var eligible_portfolio of eligible_portfolios) {
				if (eligible_portfolio.PortfolioId === this.props.initial_portfolio_id) {
					portfolio_id = this.props.initial_portfolio_id;
					break;
				}
			}

			if (!portfolio_id) {
				if (eligible_portfolios.length > 0) {
					portfolio_id = eligible_portfolios[0].PortfolioId;
				}
			}
		}

		const set_portfolio_id = (pid) => {
			this.setState({portfolio_id: pid});
		};

		if (eligible_portfolios.length > 0) {
			return <TradePortfolioOptions {...this.props} portfolio_id={portfolio_id} set_portfolio_id={set_portfolio_id}/>
		} else {
			const goto_new_game = () => {
				ModalClose();
				R.go("/new_game");
			}
			return (
				<div className="ad_trade_bg p-4">
					<T>
					<div>
					You're not currently playing any game where this stock can be traded.
					</div>
					<div className="mt-2">
					<ActionLink on_click={goto_new_game} className="ad_new_game_button ad_rbtn_25px py-2 px-5">Start a game</ActionLink>
					</div>
					</T>
				</div>
			);
		}
	}
}

var TradeOptions = ApifyParallel(
	(Apify("/z/trade_options")
		.with_query_from_props(props => ({exchange: props.exchange, ticker: props.ticker}))
		.with_response_prop("trade_options")
		.with_refresh_interval(30)
	),
	(Apify("/a/eligible_portfolios")
		.with_predicate(props => !!props.me)
		.with_query_from_props(props => ({exchange: props.exchange, ticker: props.ticker}))
		.with_response_prop("eligible_portfolios")
	)
).wrap((props) => {
	var initial_portfolio_id;
	if (props.portfolio_id) {
		initial_portfolio_id = props.portfolio_id;
	} else {
		initial_portfolio_id = props.LastGamePortfolioIdVisited;
	}
	return <TradeOptionsInner {...props} initial_portfolio_id={initial_portfolio_id}/>;

});

TradeOptions = connect(
	(state) => ({me: state.me, LastGamePortfolioIdVisited: state.LastGamePortfolioIdVisited})
)(TradeOptions);

function TradeButtonInner(props) {
	var butt_code;
	const click = () => {
		TradeInit(props.exchange, props.ticker, props.portfolio_id, props.type);
	};

    const isDisabled = () => {
        if ((props.type && props.type.includes('ETF', 'Index', 'Commodity', 'Funds/Obligations') === true) && props.isUserPremium === false) {
            return true;
        }

        if (props.type && props.type === 'Commodity' && props.canTradeCommodity === false) {
            return true;
        }

        return false;
    }

	if(props.portfolio) {
		butt_code = <Button disabled={isDisabled()} className="ad_portfolio_trade_icon" onClick={click}><img alt="trade_icon" className="ad_portfolio_trade_icon_img" src="/gfx/trade-basket-0.svg"/></Button>;
	} else {
		butt_code = <Butt disabled={isDisabled()} className="ad_trade_buy_btn ad_w_25 px-5 py-3" onClick={click}><T context="Ticker page trade button">Trade</T></Butt>;
	}

	return butt_code;
}

const CanTradeCommodity = Apify("/z/trade_commodity")
    .with_query_from_props((props) => ({user_id: props.me && props.me.Id ? props.me.Id : 0}))
    .with_response_prop("canTradeCommodity")
    .wrap((props) => {
        return <TradeButtonInner {...props} />
    })

const TradeButton = (props) => {
    if (props.type === 'Commodity') {
        return <CanTradeCommodity {...props} />
    } else {
        return <TradeButtonInner {...props} />
    }
} 


function DelistButton(props) {
	const [loading, setLoading] = React.useState(false);

	const execute_delist = () => {
		const confirmed = window.confirm("Are you sure you want to delist this stock? This action will delist the stock, and force sell all stocks.");
		if (!confirmed) {
			return; 
		}

		setLoading(true);
		API.post("/a/god/delist_stock", { exchange: props.exchange, ticker: props.ticker }, (response) => {
			console.log('Response: ', response);
			setLoading(false);
			alert("Stock has been delisted successfully. Changes will show next time the service runs.");
		});
	};

	return (
		<Button onClick={execute_delist} disabled={props.post_in_flight || loading}>
			{loading ? "Processing..." : "Delist Stock"}
		</Button>
	);
}

class TradeModal extends Modal {
	mrender() {
		if (this.props.exchange === undefined) return null;

		return <TradeOptions {...this.props}/>;
	}

	get_name() {
		return "TradeModal";
	}

	get_fullscreen_bg_class() {
		return "ad_trade_modal_panel";
	}

	get_bootstrap_modal_class() {
		return "ad_trade_modal_lg";
	}
}

export {
	TradeModal,
	DelistButton,
	TradeButton,
	TradeInit,
	TradeAbort,
};



// WEBPACK FOOTER //
// ./src/c/Trade.js